import type { CategoryGroup, BudgetCategory } from './types'

/**
 * YNAB-style starter category groups. Mirrors
 * `BudgetManager.starterCategoryGroups()` on iOS — same names, same
 * order. Drift would mean a user re-seeding on web vs iOS gets a
 * different starting point.
 */

function uuid(): string {
  // Vite ships in a browser; crypto.randomUUID is universally
  // available in our deployment targets (iOS 17+, all evergreen
  // browsers). No polyfill needed.
  return crypto.randomUUID()
}

function makeCategory(name: string, groupId: string, sortOrder: number, isCC = false): BudgetCategory {
  return {
    id: uuid(),
    name,
    groupId,
    sortOrder,
    isHidden: false,
    assigned: 0,
    activity: 0,
    available: 0,
    carryOver: 0,
    goalId: null,
    note: null,
    isCreditCardPayment: isCC,
  }
}

export function starterCategoryGroups(): CategoryGroup[] {
  const immediate = uuid()
  const trueExpenses = uuid()
  const debt = uuid()
  const goals = uuid()
  const fun = uuid()
  return [
    {
      id: immediate,
      name: 'Immediate Obligations',
      sortOrder: 0,
      isHidden: false,
      categories: [
        makeCategory('Rent/Mortgage', immediate, 0),
        makeCategory('Electric', immediate, 1),
        makeCategory('Water', immediate, 2),
        makeCategory('Internet', immediate, 3),
        makeCategory('Phone', immediate, 4),
        makeCategory('Insurance', immediate, 5),
        makeCategory('Groceries', immediate, 6),
        makeCategory('Transportation', immediate, 7),
      ],
    },
    {
      id: trueExpenses,
      name: 'True Expenses',
      sortOrder: 1,
      isHidden: false,
      categories: [
        makeCategory('Auto Maintenance', trueExpenses, 0),
        makeCategory('Medical', trueExpenses, 1),
        makeCategory('Annual Subscriptions', trueExpenses, 2),
        makeCategory('Gifts', trueExpenses, 3),
        makeCategory('Home Maintenance', trueExpenses, 4),
      ],
    },
    {
      id: debt,
      name: 'Debt Payments',
      sortOrder: 2,
      isHidden: false,
      categories: [makeCategory('Credit Card Payment', debt, 0, true)],
    },
    {
      id: goals,
      name: 'Quality of Life Goals',
      sortOrder: 3,
      isHidden: false,
      categories: [
        makeCategory('Emergency Fund', goals, 0),
        makeCategory('Vacation', goals, 1),
      ],
    },
    {
      id: fun,
      name: 'Just for Fun',
      sortOrder: 4,
      isHidden: false,
      categories: [
        makeCategory('Dining Out', fun, 0),
        makeCategory('Entertainment', fun, 1),
        makeCategory('Hobbies', fun, 2),
      ],
    },
  ]
}
